import { Injectable } from '@angular/core';
import {
    PageFixFormSkipToContentComponent,
} from '@client/app/modules/page-fix/forms/skip-to-content/skip-to-content.component';
import {
    PageFixFormMisspellingComponent,
} from '@client/app/modules/page-fix/forms/misspelling/page-fix-form-misspelling.component';
import {
    PageFixFormBrokenLinkComponent,
} from '@client/app/modules/page-fix/forms/broken-link/page-fix-form-broken-link.component';
import {
    PageFixFormEditAltTextComponent,
} from '@client/app/modules/page-fix/forms/edit-alt-text/page-fix-form-edit-alt-text.component';
import {
    PageFixFormNewWindowComponent,
} from '@client/app/modules/page-fix/forms/new-window/page-fix-form-new-window.component';
import {
    PageFixFormMarqueeComponent,
} from '@client/app/modules/page-fix/forms/marquee/page-fix-form-marquee.component';
import { PageFixFormBoldComponent } from '@client/app/modules/page-fix/forms/bold/page-fix-form-bold.component';
import { PageFixFormBlinkComponent } from '@client/app/modules/page-fix/forms/blink/page-fix-form-blink.component';
import { PageFixFormItalicComponent } from '@client/app/modules/page-fix/forms/italic/page-fix-form-italic.component';
import { PageFixFormCustomComponent } from '@client/app/modules/page-fix/forms/custom/page-fix-form-custom.component';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { MonModalService } from '@client/app/modules/modals/services/mon-modal/mon-modal.service';
import { PageFixRepoService } from '@client/app/services/api/pagefix-repo/page-fix-repo.service';
import { PAGE_FIX_FIXABLE_TYPES } from '@client/app/modules/page-fix/constants/page-fix-fixable-types';
import PAGE_FIX_ACCESSIBILITY_CHECK_CONFIG from '@client/app/modules/page-fix/constants/page-fix-accessibility-check-config.json';
import { ErrorHandler } from '@client/app/core/services/error-handler/error-handler';
import { TrapFocusService } from '@client/app/modules/trap-focus/trap-focus.service';
import { NgbModalOpenerService } from '@client/app/services/ngb-modal-opener/ngb-modal-opener.service';

@Injectable({
    providedIn: 'root',
})
export class PageFixService {

    constructor (
        private translateService: TranslateService,
        private monModalService: MonModalService,
        private pageFixRepoService: PageFixRepoService,
        private errorHandler: ErrorHandler,
        private trapFocusService: TrapFocusService,
        private ngbModalOpenerService: NgbModalOpenerService,
    ) {}

    add (type: any, ngModel: any, typeId: any, subType: any, cb?: (value?: any) => void): void {
        this.trapFocusService.pauseLast();
        if (!(typeId || ngModel.id)) {
            this.errorHandler.standardMsg('Create PageFix could not detect id', 'error');
            return;
        }

        const component = this.getComponent(this.getPageFixFormComponent(type, typeId || ngModel.id));

        this.ngbModalOpenerService
            .open({
                size: 'lg',
                component,
                windowClass: type === PAGE_FIX_FIXABLE_TYPES.accessibility_check ? 'page-fix-accessibility-form' : '',
                resolve: this.getResolve({ model: ngModel, type, subType, typeId }, this.isUpgradedComponent(component)),
            })
            .result.then(
                (value: any) => {
                    if (typeof cb === 'function') {
                        return cb(value);
                    }
                },
                () => {
                    if (typeof cb === 'function') {
                        return cb();
                    }
                },
            );
    }

    update (type: any, ngModel: any, subType: any, pageFix: any, cb?: (value?: any) => void): void {
        this.trapFocusService.pauseLast();
        if (!ngModel.id) {
            this.errorHandler.standardMsg('Update PageFix could not detect id', 'error');
            return;
        }

        const component = this.getComponent(this.getPageFixFormComponent(type, ngModel.id));

        this.ngbModalOpenerService
            .open({
                size: 'lg',
                backdrop: 'static',
                component,
                windowClass: type === PAGE_FIX_FIXABLE_TYPES.accessibility_check ? 'page-fix-accessibility-form' : '',
                resolve: this.getResolve({ model: ngModel, type, subType, pageFix }, this.isUpgradedComponent(component)),
            })
            .result.then(
                (value: any) => {
                    if (typeof cb === 'function') {
                        return cb(value);
                    }
                },
                () => {
                    if (typeof cb === 'function') {
                        return cb();
                    }
                },
            );
    }

    remove (pageFix: any): Promise<any> {
        switch (pageFix.normalizeFixableType()) {
            case PAGE_FIX_FIXABLE_TYPES.misspelling:
                return this.removeSpell(pageFix);

            case PAGE_FIX_FIXABLE_TYPES.accessibility_source_code:
            case PAGE_FIX_FIXABLE_TYPES.accessibility_check:
                return this.removeAccessibility(pageFix);

            case PAGE_FIX_FIXABLE_TYPES.link:
                return this.removeBroken(pageFix);
            default:
                return this.removeCustom(pageFix);
        }
    }

    removeAll (pageFix: any): Promise<any> {
        const msg = this.translateService.getString(
            'Are you sure you want to remove <code>all</code> [[type]] PageCorrects? <br> Please type <code>Delete</code> to confirm this action',
            {
                codeTag: '<code>',
                codeTagEnd: '</code>',
                newline: '<br>',
                type: this.getPageFixApiType(pageFix.fixable_type),
            },
        );

        return this.monModalService.prompt(msg, {}).then((result: string) => {
            if ('delete' !== result.toLowerCase().trim()) {
                return;
            }

            return this.pageFixRepoService.deleteAll(pageFix);
        }, () => {});
    }

    openCustom (pageFix: any, cb?: (model?: any) => void): void {
        this.ngbModalOpenerService
            .open({
                size: 'lg',
                component: PageFixFormCustomComponent,
                resolve: {
                    pageFix,
                },
            })
            .result.then((model: any) => {
                if (typeof cb === 'function') {
                    cb(model);
                }
            }, () => {});
    }

    private removeAccessibility (pageFix: any): Promise<any> {
        const scope = {
            deleteTag: '<code>',
            deleteTagEnd: '</code>',
            newline: '<br/>',
        };
        const msg = this.translateService.getString(
            'You are about to remove PageCorrect for this check <br> Please Type [[deleteTag]]Delete[[deleteTagEnd]] to confirm this action',
            scope,
        );
        return this.monModalService.prompt(msg, {}).then((result: string) => {
            if ('delete' !== result.toLowerCase().trim()) {
                return;
            }

            return this.pageFixRepoService.destroy(pageFix.id);
        }, () => {});
    }

    private removeBroken (pageFix: any): Promise<any> {
        let type = this.translateService.getString('link');
        if (pageFix.change.attribute === 'image') {
            type = this.translateService.getString('image');
        }
        const scope = {
            deleteTag: '<code>',
            deleteTagEnd: '</code>',
            newline: '<br/>',
            type: type,
        };
        const msg = this.translateService.getString(
            'You are about to remove PageCorrect for this [[type]] <br> Please Type [[deleteTag]]Delete[[deleteTagEnd]] to confirm this action',
            scope,
        );
        return this.monModalService.prompt(msg, {}).then((result: string) => {
            if ('delete' !== result.toLowerCase().trim()) {
                return;
            }

            return this.pageFixRepoService.destroy(pageFix.id);
        }, () => {});
    }

    private removeSpell (pageFix: any): Promise<any> {
        const scope = {
            deleteTag: '<code>',
            deleteTagEnd: '</code>',
            newline: '<br/>',
        };
        const msg = this.translateService.getString(
            'You are about to remove PageCorrect for this misspelling <br> Please Type [[deleteTag]]Delete[[deleteTagEnd]] to confirm this action',
            scope,
        );
        return this.monModalService.prompt(msg, {}).then((result: string) => {
            if ('delete' !== result.toLowerCase().trim()) {
                return;
            }

            return this.pageFixRepoService.destroy(pageFix.id);
        }, () => {});
    }

    private removeCustom (pageFix: any): Promise<any> {
        const msg = this.translateService.getString(
            'Are you sure you want to remove Custom PageCorrect? <br> Please type [[deleteTag]]Delete[[deleteTagEnd]] to confirm this action',
            {
                deleteTag: '<code>',
                deleteTagEnd: '</code>',
                newline: '<br>',
            },
        );
        return this.monModalService.prompt(msg, {}).then((result: string) => {
            if ('delete' !== result.toLowerCase().trim()) {
                return;
            }

            return this.pageFixRepoService.destroy(pageFix.id);
        }, () => {});
    }

    private getPageFixApiType (type: string): string {
        switch (type) {
            case PAGE_FIX_FIXABLE_TYPES.misspelling:
                return 'Spelling';
            case PAGE_FIX_FIXABLE_TYPES.accessibility_source_code:
                return 'Accessibility Source Code';
            case PAGE_FIX_FIXABLE_TYPES.accessibility_check:
                return 'Accessibility Check';
            case PAGE_FIX_FIXABLE_TYPES.link:
                return 'Link';
            case PAGE_FIX_FIXABLE_TYPES.custom:
                return 'Custom';
            default:
                return '';
        }
    }

    private getPageFixFormComponent (type: string, id: any): string {
        switch (type) {
            case PAGE_FIX_FIXABLE_TYPES.misspelling:
                return 'pageFixFormMisspelling';
            case PAGE_FIX_FIXABLE_TYPES.link:
                return 'pageFixFormBrokenLink';
            case PAGE_FIX_FIXABLE_TYPES.accessibility_source_code:
                return 'pageFixFormEditAltText';
            case PAGE_FIX_FIXABLE_TYPES.accessibility_check:
                return this.getAccessibilityCheckComponent(id);
            default:
                return '';
        }
    }

    private getAccessibilityCheckComponent (id: any): string {
        const config = PAGE_FIX_ACCESSIBILITY_CHECK_CONFIG[id];
        if (config) {
            return config.formComponent;
        }
        return '';
    }

    private getComponent (component: string): any {
        const dictionary = {
            pageFixFormSkipToContent: PageFixFormSkipToContentComponent,
            pageFixFormMisspelling: PageFixFormMisspellingComponent,
            pageFixFormBrokenLink: PageFixFormBrokenLinkComponent,
            pageFixFormEditAltText: PageFixFormEditAltTextComponent,
            pageFixFormNewWindow: PageFixFormNewWindowComponent,
            pageFixFormMarquee: PageFixFormMarqueeComponent,
            pageFixFormBold: PageFixFormBoldComponent,
            pageFixFormBlink: PageFixFormBlinkComponent,
            PageFixFormItalic: PageFixFormItalicComponent,
        };

        return dictionary[component] ? dictionary[component] : component;
    }

    private isUpgradedComponent (component: any): boolean {
        return typeof component !== 'string';
    }

    private getResolve (objects: any, isUpgraded: boolean): any {
        if (isUpgraded) {
            return objects;
        }

        const results = {};
        for (const key of Object.keys(objects)) {
            results[key] = function () {
                return objects[key];
            };
        }

        return results;
    }
}
